import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, type Simulation,
} from 'd3-force'
import { getWirepasGatewaysAdmin, getWirepasTopology } from '@/crud/wirepas-location'
import {
  radiusOf, nodeFill, rssiToDist, rssiToStrength, linkId, buildSignature,
  layoutInfra, layoutHierarchical, computeFitZoom, POLL_MS, C_EDGE,
  type TopoNode, type SimNode, type SimLink, type TopoData,
} from '@/utils/wirepas-topology'
import { useGraphPanZoom } from './useGraphPanZoom'
import { usePolling } from './usePolling'

// The full model + engine behind the topology graph: gateway/topology fetching+polling, the
// d3-force simulation lifecycle (build/rebuild/resize), and the view-model computeds the template
// reads. Pan/zoom/drag interaction itself is delegated to useGraphPanZoom (a separate concern).
export function useWirepasTopologyGraph(
  props: { active: boolean },
  panelEl: Ref<HTMLDivElement | null>,
  svgEl: Ref<SVGSVGElement | null>,
) {
  const { t } = useI18n()

  const gateways = ref<any[]>([])
  const gwId = ref('')
  const data = ref<TopoData | null>(null)
  const error = ref<string | null>(null)
  const loading = ref(true)
  const sel = ref<string | null>(null)
  const q = ref('')
  const mode = ref<'organic' | 'hierarchical'>('organic')
  const dims = ref({ w: 1120, h: 620 })
  const renderNodes = ref<SimNode[]>([])
  const renderLinks = ref<SimLink[]>([])
  // Tags with zero links (no sighting in 2h+ -- "departed") don't get an SVG node at all; they're
  // listed in a plain table below the graph instead of fighting for space/legibility inside it.
  const departedTags = ref<TopoNode[]>([])
  // A live tag IS in range, just excluded from the graph because the backend caps how many live
  // tags get edges (too many to force-simulate/render without hanging the tab) -- also listed, but
  // as a distinct reason from departed.
  const overflowTags = ref<TopoNode[]>([])

  let sim: Simulation<SimNode, SimLink> | null = null
  let simNodes: SimNode[] = []
  let simLinks: SimLink[] = []
  let lastSig = ''
  let lastBottomReserve = 0
  let parkLabelsState: { tags: { x: number; y: number } | null; anchors: { x: number; y: number } | null } = { tags: null, anchors: null }
  const parkLabels = ref(parkLabelsState)

  const bump = () => {
    renderNodes.value = simNodes.slice()
    renderLinks.value = simLinks.slice()
  }

  const empty = computed(() => !data.value || data.value.nodes.length <= 1)
  const offAnchorCount = computed(() => data.value ? data.value.nodes.filter((n) => n.kind === 'anchor' && n.active === false).length : 0)

  const nodeKindLabel = (kind: string) => t(`components.infrastructure.topology.detail.${kind}`)
  const linkNode = (e: string | number | SimNode): SimNode | null => (typeof e === 'object' ? (e as SimNode) : null)
  const edgeColor = (l: SimLink) => (linkId(l.source).startsWith('tag:') ? nodeFill(linkNode(l.source)!) : C_EDGE)

  const topoLink = (n: TopoNode): string | null => {
    if (n.kind === 'tag') return `/wirepas-assets/${n.id.replace(/^tag:/, '')}`
    if (n.kind === 'gateway' && n.assignedLocationId != null) return `/wirepas-locations/${n.assignedLocationId}/detail`
    return null
  }

  const matchSet = computed(() => {
    const ql = q.value.trim().toLowerCase()
    const s = new Set<string>()
    for (const n of renderNodes.value) {
      if (n.kind !== 'tag') continue
      if (!ql || n.label.toLowerCase().includes(ql) || (n.addr || '').toLowerCase().includes(ql)) s.add(n.id)
    }
    return s
  })
  const neighborSet = computed(() => {
    const s = new Set<string>()
    if (!sel.value) return s
    s.add(sel.value)
    for (const l of renderLinks.value) {
      const sId = linkId(l.source), tId = linkId(l.target)
      if (sId === sel.value) s.add(tId)
      else if (tId === sel.value) s.add(sId)
    }
    return s
  })
  const nodeDimmed = (n: TopoNode) => (sel.value ? !neighborSet.value.has(n.id) : n.kind === 'tag' && !!q.value.trim() && !matchSet.value.has(n.id))
  const edgeDimmed = (l: SimLink) => {
    const sId = linkId(l.source), tId = linkId(l.target)
    return sel.value ? !(sId === sel.value || tId === sel.value) : sId.startsWith('tag:') && !!q.value.trim() && !matchSet.value.has(sId)
  }
  const selNode = computed(() => (sel.value ? renderNodes.value.find((n) => n.id === sel.value) ?? null : null))

  const fitView = () => {
    const z = computeFitZoom(simNodes, parkLabelsState, dims.value)
    if (z) panZoom.zoom.value = z
  }

  // Tag fx/fy pins left over from Hierarchical (which pins every node) mean nothing in Organic
  // mode -- release them so the tag rejoins the simulation. (Departed tags never reach simNodes.)
  const releaseTagPins = () => {
    for (const n of simNodes) {
      if (n.kind === 'tag') { n.fx = null; n.fy = null }
    }
  }

  // Only rebuilds when the node/edge set changed; a poll tick just refreshes fields in place.
  const rebuildGraph = () => {
    const d = data.value
    if (!d) return

    // Independent of the fast-path below: which tags have zero links can only change when the
    // structural signature changes anyway, but battery/label can drift on every poll. The backend
    // already caps how many live tags get edges (too many to force-simulate) -- a linkless tag is
    // "departed" if it's actually lost signal, otherwise it's live but excluded for capacity.
    const linkedTagIds = new Set(d.links.map((l) => l.source))
    const linkless = d.nodes.filter((n) => n.kind === 'tag' && !linkedTagIds.has(n.id))
    departedTags.value = linkless.filter((n) => n.status === 'departed')
    overflowTags.value = linkless.filter((n) => n.status !== 'departed')

    const sig = buildSignature(d, mode.value)
    if (sig === lastSig) {
      const fresh = new Map(d.nodes.map((n) => [n.id, n]))
      for (const n of simNodes) Object.assign(n, fresh.get(n.id), { x: n.x, y: n.y, fx: n.fx, fy: n.fy, vx: n.vx, vy: n.vy })
      // Structure is unchanged, but per-edge rssi can still drift between polls.
      const freshRssi = new Map(d.links.map((l) => [`${l.source}>${l.target}`, l.rssi]))
      for (const l of simLinks) {
        const key = `${linkId(l.source)}>${linkId(l.target)}`
        if (freshRssi.has(key)) l.rssi = freshRssi.get(key)
      }
      bump()
      return
    }
    lastSig = sig

    const prev = new Map(simNodes.map((n) => [n.id, n]))
    // Departed and over-the-cap tags never become SVG nodes -- both are rendered as plain lists instead.
    const nodes: SimNode[] = d.nodes
      .filter((n) => n.kind !== 'tag' || linkedTagIds.has(n.id))
      .map((n) => {
        const p = prev.get(n.id)
        return { ...n, x: p?.x, y: p?.y, vx: p?.vx, vy: p?.vy, fx: p?.fx, fy: p?.fy }
      })
    const byId = new Map(nodes.map((n) => [n.id, n]))
    const links: SimLink[] = d.links.filter((l) => byId.has(l.source) && byId.has(l.target)).map((l) => ({ ...l }))

    const { w, h } = dims.value
    const parkedAnchors = nodes.filter((n) => n.kind === 'anchor' && n.active === false)

    // A single-row layout for a large parked list forces fitView to zoom out to fit one absurdly
    // wide line, shrinking the whole live graph with it -- wrap into a grid instead so the parked
    // bucket's footprint stays roughly square regardless of how many nodes land in it.
    const PARK_GAP = 48
    const PARK_ROW_H = 34
    const PARK_LEFT_PAD = 270
    const maxPerRow = Math.max(1, Math.floor((w - PARK_LEFT_PAD - 60) / PARK_GAP))
    const anchorRows = Math.ceil(parkedAnchors.length / maxPerRow) || 0
    const bottomReserve = anchorRows ? 60 + anchorRows * PARK_ROW_H : 0
    lastBottomReserve = bottomReserve

    if (mode.value === 'organic') layoutInfra(nodes, dims.value, bottomReserve, links)
    else layoutHierarchical(nodes, links, dims.value, bottomReserve)

    const parkGrid = (list: SimNode[], yStart: number) => {
      list.forEach((n, i) => {
        n.fx = PARK_LEFT_PAD + (i % maxPerRow) * PARK_GAP
        n.fy = yStart + Math.floor(i / maxPerRow) * PARK_ROW_H
        n.x = n.fx; n.y = n.fy
      })
    }
    const anchorStartY = h - anchorRows * PARK_ROW_H - 6
    parkGrid(parkedAnchors, anchorStartY)
    parkLabelsState = {
      tags: null,
      anchors: parkedAnchors.length ? { x: 28, y: anchorStartY } : null,
    }
    parkLabels.value = parkLabelsState

    // Hierarchical: no simulation, resolve link endpoints to objects ourselves.
    if (mode.value === 'hierarchical') {
      for (const l of links) {
        if (typeof l.source === 'string') l.source = byId.get(l.source) as unknown as SimNode
        if (typeof l.target === 'string') l.target = byId.get(l.target) as unknown as SimNode
      }
      sim?.stop()
      sim = null
      simNodes = nodes
      simLinks = links
      bump()
      if (!panZoom.userMoved.value) fitView()
      return
    }

    // Single-hub gateway: relax links and increase repulsion so tags fan out.
    const tagLinks = links.filter((l) => byId.get(l.source as unknown as string)?.kind === 'tag')
    const singleHub = tagLinks.every((l) => byId.get(l.target as unknown as string)?.kind === 'gateway')
    const linkStrength = (rssi?: number | null) => rssiToStrength(rssi) * (singleHub ? 0.25 : 1)

    sim?.stop()
    const newSim = forceSimulation<SimNode>(nodes)
      .force('link', forceLink<SimNode, SimLink>(links)
        .id((n) => n.id)
        .distance((l) => ((l.source as SimNode).kind === 'tag' ? rssiToDist(l.rssi) : 0))
        .strength((l) => ((l.source as SimNode).kind === 'tag' ? linkStrength(l.rssi) : 0)))
      .force('charge', forceManyBody<SimNode>().strength((d2) => (d2.kind === 'tag' ? (singleHub ? -700 : -320) : 0)))
      .force('center', forceCenter(dims.value.w / 2, dims.value.h / 2).strength(0.008))
      .force('collide', forceCollide<SimNode>((d2) => radiusOf(d2) + (d2.kind === 'tag' ? 44 : 16)).strength(0.95))
      .alpha(1)
      .alphaDecay(0.03)
    // Re-fit periodically while settling too, not just on 'end' -- a large single-hub cloud can
    // take a while to reach d3's alpha-decay threshold, and staying at the stale initial zoom
    // until then leaves the view badly cropped or full of dead space in the meantime.
    let tickCount = 0
    newSim.on('tick', () => {
      bump()
      tickCount += 1
      if (!panZoom.userMoved.value && tickCount % 15 === 0) fitView()
    })
    newSim.on('end', () => { if (!panZoom.userMoved.value) fitView() })
    sim = newSim
    simNodes = nodes
    simLinks = links
    bump()
  }

  // ── Interaction (pan/zoom/drag) delegated to a decoupled composable ─────
  const panZoom = useGraphPanZoom<SimNode>({
    svgEl,
    dims,
    onDragStart: (n) => { n.fx = n.x; n.fy = n.y; sim?.alphaTarget(0.3).restart() },
    onDrag: (n, x, y) => { n.fx = x; n.fy = y; n.x = x; n.y = y; bump() },
    onDragEnd: () => { sim?.alphaTarget(0) },
    onSelectNode: (n) => { sel.value = n.id },
    onSelectBackground: () => { sel.value = null },
  })

  const resetView = () => {
    panZoom.userMoved.value = false
    if (mode.value === 'hierarchical') {
      layoutHierarchical(simNodes, simLinks, dims.value, lastBottomReserve)
      bump()
      fitView()
      return
    }
    releaseTagPins()
    layoutInfra(simNodes, dims.value, lastBottomReserve)
    sim?.alpha(0.85).restart()
    fitView()
  }

  // ── Data fetching ─────────────────────────────────────────────────────
  const fetchGateways = () => {
    // The gateway picker needs every gateway, not one page -- wirepas-gateways is paginated
    // (default take=20) now that the network grid drives it server-side.
    getWirepasGatewaysAdmin({ take: 1000 }).then((res: any) => {
      gateways.value = res.data?.result ?? []
      if (!gwId.value && gateways.value.length) {
        gwId.value = gateways.value.find((g: any) => g.state === 'online')?.gwId ?? gateways.value[0].gwId
      }
    }).catch((e: any) => { error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error') })
  }
  const fetchTopology = () => {
    if (!gwId.value) return
    const requestedGw = gwId.value
    getWirepasTopology(requestedGw).then((res: any) => {
      if (requestedGw !== gwId.value) return
      data.value = res.data?.data ?? null
      loading.value = false
      rebuildGraph()
    }).catch((e: any) => {
      if (requestedGw !== gwId.value) return
      error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
      loading.value = false
    })
  }
  // Also re-polls the gateway list so the dropdown's online/offline state doesn't go stale.
  const polling = usePolling(() => { fetchTopology(); fetchGateways() }, POLL_MS)

  // Re-measures and re-lays-out only if the size actually changed.
  const applyDims = () => {
    const el = panelEl.value
    if (!el) return
    const w = Math.round(el.clientWidth), h = Math.round(el.clientHeight)
    if (w < 50 || h < 50) return
    const changed = Math.abs(w - dims.value.w) > 1 || Math.abs(h - dims.value.h) > 1
    if (!changed) return
    dims.value = { w, h }
    if (mode.value === 'hierarchical') {
      layoutHierarchical(simNodes, simLinks, dims.value, lastBottomReserve)
      bump()
    } else {
      layoutInfra(simNodes, dims.value, lastBottomReserve)
      if (sim) {
        sim.force('center', forceCenter(w / 2, h / 2).strength(0.008))
        sim.alpha(0.4).restart()
      }
    }
    if (!panZoom.userMoved.value) fitView()
  }

  watch(gwId, () => {
    panZoom.userMoved.value = false
    sel.value = null
    error.value = null
    loading.value = true
    lastSig = ''
    fetchTopology()
  })
  watch(mode, () => {
    panZoom.userMoved.value = false
    if (mode.value === 'organic') releaseTagPins()
    rebuildGraph()
  })
  watch(() => props.active, (isActive) => {
    if (isActive) {
      // ResizeObserver doesn't reliably fire on display:none→visible, so remeasure explicitly.
      nextTick(() => applyDims())
      if (!gateways.value.length) fetchGateways()
      fetchTopology()
      polling.start()
    } else {
      polling.stop()
      sim?.stop()
    }
  }, { immediate: true })

  onMounted(() => {
    const el = panelEl.value
    if (el) {
      applyDims()
      const ro = new ResizeObserver(applyDims)
      ro.observe(el)
      onBeforeUnmount(() => ro.disconnect())
    }
  })
  onBeforeUnmount(() => { sim?.stop() })

  return {
    gateways, gwId, error, loading, sel, q, mode, dims, renderNodes, renderLinks, departedTags, overflowTags,
    empty, offAnchorCount, parkLabels,
    matchSet, selNode, nodeKindLabel, edgeColor, linkNode, topoLink,
    nodeDimmed, edgeDimmed, resetView,
    zoom: panZoom.zoom, onNodeDown: panZoom.onNodeDown, onSvgDown: panZoom.onSvgDown,
    onMove: panZoom.onMove, onUp: panZoom.onUp, zoomBy: panZoom.zoomBy,
  }
}
